import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Search, QrCode, Trash2, Pencil, Printer, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const EMPTY = {
  first_name: "", last_name: "", employee_no: "", tc_kimlik: "", phone: "", email: "",
  department: "", position: "", salary: 0, hire_date: "", weekly_leave_days: 1,
  work_start: "", work_end: "",
  photo_url: "", is_active: true, pin: "", create_login: false, password: "",
};

const AVATARS = [
  "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg",
  "https://images.pexels.com/photos/31869537/pexels-photo-31869537.jpeg",
  "https://images.pexels.com/photos/30496628/pexels-photo-30496628.jpeg",
];

export default function Employees() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [qrDlg, setQrDlg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", { params: q ? { q } : {} });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        delete payload.create_login;
        delete payload.password;
        await api.patch(`/employees/${editing}`, payload);
        toast.success("Personel güncellendi");
      } else {
        await api.post("/employees", form);
        toast.success("Personel eklendi");
      }
      setOpen(false); setForm(EMPTY); setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Bu personeli silmek istediğinizden emin misiniz?")) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success("Personel silindi");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openEdit = (emp) => {
    setEditing(emp.id);
    setForm({ ...EMPTY, ...emp });
    setOpen(true);
  };

  const printQR = (emp) => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>QR - ${emp.first_name} ${emp.last_name}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:40px;} h2{margin:12px 0 4px} img{width:280px;height:280px} .pin{font-size:36px;letter-spacing:6px;font-weight:800;margin-top:16px;font-family:monospace}</style>
      </head><body>
        <h2>${emp.first_name} ${emp.last_name}</h2>
        <div>Personel No: ${emp.employee_no}</div>
        <img src="${emp.qr_png}" />
        <div>PIN Kodu</div>
        <div class="pin">${emp.pin || "-"}</div>
        <p style="font-family:monospace;font-size:11px;color:#666">${emp.qr_uuid}</p>
        <script>window.onload=()=>window.print()</script>
      </body></html>`);
    win.document.close();
  };

  const canEdit = user?.role === "admin" || user?.role === "manager";

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Personel</h1>
          <p className="text-muted-foreground text-sm mt-1">Personel yönetimi ve QR kod üretimi</p>
        </div>
        {canEdit && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(EMPTY); }}}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-btn" className="rounded-lg gap-2">
                <Plus className="w-4 h-4" /> Yeni Personel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editing ? "Personel Düzenle" : "Yeni Personel"}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                {[
                  ["first_name", "Ad", "text"],
                  ["last_name", "Soyad", "text"],
                  ["employee_no", "Personel No", "text"],
                  ["tc_kimlik", "TC Kimlik No", "text"],
                  ["phone", "Telefon", "text"],
                  ["email", "E-posta", "email"],
                  ["department", "Departman", "text"],
                  ["position", "Görev", "text"],
                  ["salary", "Maaş", "number"],
                  ["hire_date", "İşe Giriş", "date"],
                  ["weekly_leave_days", "Haftalık İzin (gün)", "number"],
                  ["work_start", "Özel Mesai Başlangıcı (boş = genel)", "time"],
                  ["work_end", "Özel Mesai Bitişi (boş = genel)", "time"],
                  ["pin", "PIN (boş bırakırsanız otomatik üretilir)", "text"],
                ].map(([key, label, type]) => (
                  <div key={key} className="space-y-1.5">
                    <Label>{label}</Label>
                    <Input
                      data-testid={`emp-${key}`}
                      type={type}
                      value={form[key] ?? ""}
                      onChange={(e) => setForm({ ...form, [key]: type === "number" ? Number(e.target.value) : e.target.value })}
                    />
                  </div>
                ))}
                <div className="space-y-1.5 col-span-2">
                  <Label>Fotoğraf URL (opsiyonel)</Label>
                  <Input data-testid="emp-photo" value={form.photo_url || ""} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} />
                  <div className="flex gap-2 mt-2">
                    {AVATARS.map((a) => (
                      <button key={a} type="button" onClick={() => setForm({ ...form, photo_url: a })}>
                        <img src={a} alt="" className={`w-12 h-12 rounded-full object-cover border-2 ${form.photo_url === a ? "border-blue-500" : "border-transparent"}`} />
                      </button>
                    ))}
                  </div>
                </div>
                {!editing && (
                  <>
                    <div className="col-span-2 flex items-center gap-2 pt-2 border-t">
                      <input
                        id="cl" type="checkbox"
                        data-testid="emp-create-login"
                        checked={form.create_login}
                        onChange={(e) => setForm({ ...form, create_login: e.target.checked })}
                      />
                      <Label htmlFor="cl">Personel için sistem hesabı oluştur (e-posta + şifre ile giriş yapabilsin)</Label>
                    </div>
                    {form.create_login && (
                      <div className="space-y-1.5 col-span-2">
                        <Label>Şifre</Label>
                        <Input
                          data-testid="emp-password"
                          type="password"
                          value={form.password}
                          onChange={(e) => setForm({ ...form, password: e.target.value })}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
                <Button data-testid="emp-submit" onClick={submit}>Kaydet</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="glass-card rounded-xl p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="employee-search"
            placeholder="Ad, soyad, personel no veya telefon ara..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="employees-list">
        {items.map((emp) => (
          <motion.div key={emp.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-5" data-testid={`emp-card-${emp.employee_no}`}>
            <div className="flex items-start gap-4">
              {emp.photo_url ? (
                <img src={emp.photo_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-secondary flex items-center justify-center font-display font-bold text-xl">
                  {emp.first_name?.[0]}{emp.last_name?.[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold tracking-tight truncate">{emp.first_name} {emp.last_name}</div>
                <div className="text-xs text-muted-foreground font-mono">#{emp.employee_no}</div>
                <div className="text-xs text-muted-foreground truncate mt-1">{emp.department} · {emp.position}</div>
                <div className="mt-2 flex items-center gap-2">
                  <Badge variant={emp.is_active ? "default" : "secondary"} className="text-[10px]">
                    {emp.is_active ? "AKTİF" : "PASİF"}
                  </Badge>
                  {emp.pin && (
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-secondary text-muted-foreground" data-testid={`emp-pin-${emp.employee_no}`}>
                      PIN {emp.pin}
                    </span>
                  )}
                  {(emp.work_start || emp.work_end) && (
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-blue-500/10 text-blue-500" data-testid={`emp-hours-${emp.employee_no}`}>
                      {emp.work_start || "—"}–{emp.work_end || "—"}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border/60">
              <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => setQrDlg(emp)} data-testid={`qr-${emp.employee_no}`}>
                <QrCode className="w-3.5 h-3.5" /> QR
              </Button>
              {canEdit && (
                <>
                  <Button size="sm" variant="outline" onClick={() => openEdit(emp)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {user.role === "admin" && (
                    <Button size="sm" variant="outline" onClick={() => del(emp.id)} className="text-red-500 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-16">Henüz personel eklenmemiş.</div>
        )}
      </div>

      <Dialog open={!!qrDlg} onOpenChange={(o) => !o && setQrDlg(null)}>
        <DialogContent className="max-w-md">
          {qrDlg && (
            <>
              <DialogHeader>
                <DialogTitle>{qrDlg.first_name} {qrDlg.last_name}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-3 py-4">
                <img src={qrDlg.qr_png} alt="QR" className="w-56 h-56 bg-white p-3 rounded-xl" data-testid="qr-preview" />
                <div className="w-full mt-2 p-4 rounded-xl bg-secondary border border-border/60 text-center">
                  <div className="text-xs uppercase tracking-wider text-muted-foreground">PIN Kodu</div>
                  <div className="font-mono text-3xl font-bold tracking-wider mt-1" data-testid="qr-pin">{qrDlg.pin || "—"}</div>
                  <button
                    className="text-xs text-blue-500 hover:text-blue-400 mt-2"
                    data-testid="regen-pin"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/employees/${qrDlg.id}/reset-pin`, {});
                        setQrDlg(data);
                        setItems((prev) => prev.map((x) => (x.id === data.id ? data : x)));
                        toast.success("Yeni PIN üretildi");
                      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
                    }}
                  >Yeni PIN üret</button>
                </div>
                <div className="text-xs text-muted-foreground font-mono break-all text-center">{qrDlg.qr_uuid}</div>
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <a href={qrDlg.qr_png} download={`qr-${qrDlg.employee_no}.png`}>
                  <Button variant="outline" className="gap-2"><Download className="w-4 h-4" />İndir</Button>
                </a>
                <Button onClick={() => printQR(qrDlg)} className="gap-2"><Printer className="w-4 h-4" />Yazdır</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
